/**
 * Super Admin - Create New User
 * Dedicated page for creating new user accounts with all required fields
 */

import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

// @mui material components
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import TextField from "@mui/material/TextField";
import Select from "@mui/material/Select";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import CircularProgress from "@mui/material/CircularProgress";
import Alert from "@mui/material/Alert";
import Grid from "@mui/material/Grid";
import Avatar from "@mui/material/Avatar";
import Button from "@mui/material/Button";
import InputAdornment from "@mui/material/InputAdornment";
import MenuItem from "@mui/material/MenuItem";
import Chip from "@mui/material/Chip";
import Divider from "@mui/material/Divider";
import Paper from "@mui/material/Paper";
import Box from "@mui/material/Box";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";

// Material Dashboard 2 React examples
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";

// API
import { superAdminAPI } from "utils/api";
import { NATIONALITIES } from "constants/nationalities";

function CreateUser() {
  const navigate = useNavigate();
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState({ type: "", text: "" });
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    password: "",
    role: "",
    idNumber: "",
    phone: "",
    companyNumber: "",
    relevantPhoneNumbers: ["", ""],
    address: "",
    maritalStatus: "Single",
    familyMembersNumber: 0,
    personalEmail: "",
    nationality: "",
    gender: "Male",
  });
  const [photoFile, setPhotoFile] = useState(null);
  const [idPhotoFile, setIdPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);

  useEffect(() => {
    fetchRoles();
  }, []);

  const fetchRoles = async () => {
    try {
      setLoading(true);
      const response = await superAdminAPI.getAvailableRoles();
      setRoles(response.data || []);
    } catch (error) {
      console.error("Error fetching roles:", error);
      setMessage({
        type: "error",
        text: "Failed to load roles. Please try again.",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name.startsWith("relevantPhone")) {
      const index = parseInt(name.replace("relevantPhone", ""));
      const newPhones = [...formData.relevantPhoneNumbers];
      newPhones[index] = value;
      setFormData({ ...formData, relevantPhoneNumbers: newPhones });
    } else {
      setFormData({ ...formData, [name]: value });
    }
    // Clear error message when user starts typing
    if (message.text) setMessage({ type: "", text: "" });
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith("image/")) {
        setMessage({ type: "error", text: "Please select an image file for photo" });
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        setMessage({ type: "error", text: "Photo size must be less than 5MB" });
        return;
      }
      setPhotoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotoPreview(reader.result);
      };
      reader.readAsDataURL(file);
      setMessage({ type: "", text: "" });
    }
  };

  const handleIdPhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.type !== "application/pdf") {
        setMessage({ type: "error", text: "ID photo must be a PDF file" });
        return;
      }
      if (file.size > 10 * 1024 * 1024) {
        setMessage({ type: "error", text: "ID photo size must be less than 10MB" });
        return;
      }
      setIdPhotoFile(file);
      setMessage({ type: "", text: "" });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (
      !formData.fullName ||
      !formData.email ||
      !formData.role ||
      !formData.idNumber ||
      !formData.companyNumber ||
      !formData.gender
    ) {
      setMessage({
        type: "error",
        text: "Please fill in all required fields (Full Name, Email, Role, ID Number, Company Number, Gender).",
      });
      return;
    }

    if (!photoFile) {
      setMessage({
        type: "error",
        text: "Photo is mandatory. Please upload a photo.",
      });
      return;
    }

    if (!idPhotoFile) {
      setMessage({
        type: "error",
        text: "ID Photo is mandatory. Please upload an ID photo PDF.",
      });
      return;
    }

    try {
      setSubmitting(true);
      setMessage({ type: "", text: "" });

      const selectedRole = roles.find((r) => r._id === formData.role);
      if (!selectedRole) {
        setMessage({
          type: "error",
          text: "Please select a valid role",
        });
        return;
      }

      // Create FormData for file uploads
      const submitData = new FormData();
      submitData.append("fullName", formData.fullName);
      submitData.append("email", formData.email);
      submitData.append("roleName", selectedRole.name);
      submitData.append("idNumber", formData.idNumber);
      submitData.append("companyNumber", formData.companyNumber);
      submitData.append("gender", formData.gender);

      if (formData.password) submitData.append("password", formData.password);
      if (formData.phone) submitData.append("phone", formData.phone);
      // Send relevant phone numbers as clean array (filter out empty values)
      const cleanPhones = [formData.relevantPhoneNumbers[0], formData.relevantPhoneNumbers[1]]
        .map((p) => String(p || "").trim())
        .filter((p) => p !== "");
      if (cleanPhones.length > 0) {
        submitData.append("relevantPhoneNumbers", JSON.stringify(cleanPhones));
      }
      if (formData.address) submitData.append("address", formData.address);
      if (formData.maritalStatus) submitData.append("maritalStatus", formData.maritalStatus);
      if (formData.familyMembersNumber)
        submitData.append("familyMembersNumber", formData.familyMembersNumber);
      if (formData.personalEmail) submitData.append("personalEmail", formData.personalEmail);
      if (formData.nationality) submitData.append("nationality", formData.nationality);

      if (photoFile) submitData.append("photo", photoFile);
      if (idPhotoFile) submitData.append("idPhoto", idPhotoFile);

      const response = await superAdminAPI.createUserAccount(submitData);
      if (response.success) {
        setMessage({
          type: "success",
          text: "User created successfully! Credentials have been sent via email.",
        });
        // Redirect to users list after 2 seconds
        setTimeout(() => {
          navigate("/super-admin/users");
        }, 2000);
      }
    } catch (error) {
      console.error("Error creating user:", error);
      setMessage({
        type: "error",
        text: error.message || "Failed to create user. Please try again.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
          <CircularProgress />
        </MDBox>
        <Footer />
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        {/* Header */}
        <MDBox mb={3}>
          <MDBox display="flex" alignItems="center" mb={2}>
            <MDButton
              variant="outlined"
              color="secondary"
              onClick={() => navigate("/super-admin/users")}
              startIcon={<Icon>arrow_back</Icon>}
              sx={{
                mr: 2,
                color: "white",
                borderColor: "white",
                "&:hover": {
                  color: "white",
                  borderColor: "white",
                  backgroundColor: "rgba(255, 255, 255, 0.1)",
                },
              }}
            >
              Back to Users
            </MDButton>
            <MDTypography variant="h4" fontWeight="bold">
              Create New User
            </MDTypography>
          </MDBox>
          <MDTypography variant="body2" color="text">
            Fill in all required fields to create a new user account. All fields marked with * are
            mandatory.
          </MDTypography>
        </MDBox>

        {/* Message Alert */}
        {message.text && (
          <MDBox mb={3}>
            <Alert
              severity={message.type}
              onClose={() => setMessage({ type: "", text: "" })}
              sx={{ borderRadius: 2 }}
            >
              {message.text}
            </Alert>
          </MDBox>
        )}

        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            {/* Left Column - Photo and ID Photo */}
            <Grid item xs={12} md={3}>
              <Card sx={{ position: "sticky", top: 20 }}>
                <MDBox p={3}>
                  <MDTypography variant="h6" fontWeight="medium" mb={2}>
                    Profile Documents
                  </MDTypography>

                  {/* Photo Section */}
                  <MDBox mb={3}>
                    <MDTypography
                      variant="caption"
                      color="text.secondary"
                      mb={1}
                      display="block"
                      fontWeight="medium"
                    >
                      Photo <span style={{ color: "red" }}>*</span>
                    </MDTypography>
                    <MDBox display="flex" flexDirection="column" alignItems="center">
                      <Avatar
                        src={photoPreview}
                        alt="Profile Photo"
                        sx={{
                          width: 150,
                          height: 150,
                          mb: 2,
                          border: "3px solid",
                          borderColor: photoPreview ? "success.main" : "grey.300",
                          boxShadow: 2,
                        }}
                      />
                      <MDBox display="flex" flexDirection="column" gap={1} width="100%">
                        <Button
                          variant={photoPreview ? "outlined" : "contained"}
                          component="label"
                          size="medium"
                          startIcon={<Icon>{photoPreview ? "edit" : "photo_camera"}</Icon>}
                          disabled={submitting}
                          color={photoPreview ? "success" : "primary"}
                          fullWidth
                          sx={{
                            color: photoPreview ? "success.main" : "white !important",
                            backgroundColor: photoPreview ? "transparent" : undefined,
                            borderColor: photoPreview ? "success.main" : undefined,
                            "&:hover": {
                              color: photoPreview ? "success.main" : "white !important",
                              backgroundColor: photoPreview ? "rgba(76, 175, 80, 0.1)" : undefined,
                              borderColor: photoPreview ? "success.main" : undefined,
                            },
                            "& .MuiButton-startIcon": {
                              color: photoPreview ? "success.main" : "white !important",
                            },
                          }}
                        >
                          {photoPreview ? "Change Photo" : "Upload Photo"}
                          <input type="file" hidden accept="image/*" onChange={handlePhotoChange} />
                        </Button>
                        {photoPreview && (
                          <Button
                            variant="outlined"
                            color="error"
                            size="small"
                            startIcon={<Icon>delete</Icon>}
                            onClick={() => {
                              setPhotoFile(null);
                              setPhotoPreview(null);
                            }}
                            disabled={submitting}
                            fullWidth
                            sx={{
                              color: "error.main",
                              borderColor: "error.main",
                              backgroundColor: "transparent",
                              "&:hover": {
                                color: "error.main",
                                borderColor: "error.main",
                                backgroundColor: "rgba(211, 47, 47, 0.1)",
                              },
                            }}
                          >
                            Remove Photo
                          </Button>
                        )}
                        {photoFile && (
                          <MDTypography variant="caption" color="text.secondary" textAlign="center">
                            {photoFile.name}
                          </MDTypography>
                        )}
                        {!photoPreview && (
                          <MDTypography variant="caption" color="error" textAlign="center" mt={1}>
                            Photo is required
                          </MDTypography>
                        )}
                      </MDBox>
                    </MDBox>
                  </MDBox>

                  <Divider sx={{ my: 2 }} />

                  {/* ID Photo Section */}
                  <MDBox>
                    <MDTypography
                      variant="caption"
                      color="text.secondary"
                      mb={1}
                      display="block"
                      fontWeight="medium"
                    >
                      ID Photo (PDF) <span style={{ color: "red" }}>*</span>
                    </MDTypography>
                    <MDBox display="flex" flexDirection="column" alignItems="center">
                      {idPhotoFile ? (
                        <MDBox mb={2} textAlign="center">
                          <Chip
                            icon={<Icon>description</Icon>}
                            label={idPhotoFile.name}
                            color="success"
                            size="medium"
                            sx={{ mb: 1 }}
                          />
                          <MDTypography variant="caption" color="text.secondary" display="block">
                            PDF uploaded
                          </MDTypography>
                        </MDBox>
                      ) : (
                        <MDBox mb={2} textAlign="center">
                          <Chip
                            icon={<Icon>description</Icon>}
                            label="No ID Photo"
                            color="error"
                            size="medium"
                            sx={{ mb: 1 }}
                          />
                          <MDTypography variant="caption" color="error" display="block">
                            Required
                          </MDTypography>
                        </MDBox>
                      )}
                      <MDBox display="flex" flexDirection="column" gap={1} width="100%">
                        <Button
                          variant={idPhotoFile ? "outlined" : "contained"}
                          component="label"
                          size="medium"
                          startIcon={<Icon>upload_file</Icon>}
                          disabled={submitting}
                          color={idPhotoFile ? "success" : "primary"}
                          fullWidth
                          sx={{
                            color: idPhotoFile ? "success.main" : "white !important",
                            backgroundColor: idPhotoFile ? "transparent" : undefined,
                            borderColor: idPhotoFile ? "success.main" : undefined,
                            "&:hover": {
                              color: idPhotoFile ? "success.main" : "white !important",
                              backgroundColor: idPhotoFile ? "rgba(76, 175, 80, 0.1)" : undefined,
                              borderColor: idPhotoFile ? "success.main" : undefined,
                            },
                            "& .MuiButton-startIcon": {
                              color: idPhotoFile ? "success.main" : "white !important",
                            },
                          }}
                        >
                          {idPhotoFile ? "Change ID Photo" : "Upload ID Photo"}
                          <input
                            type="file"
                            hidden
                            accept="application/pdf"
                            onChange={handleIdPhotoChange}
                          />
                        </Button>
                        {idPhotoFile && (
                          <Button
                            variant="outlined"
                            color="error"
                            size="small"
                            startIcon={<Icon>delete</Icon>}
                            onClick={() => {
                              setIdPhotoFile(null);
                            }}
                            disabled={submitting}
                            fullWidth
                            sx={{
                              color: "error.main",
                              borderColor: "error.main",
                              backgroundColor: "transparent",
                              "&:hover": {
                                color: "error.main",
                                borderColor: "error.main",
                                backgroundColor: "rgba(211, 47, 47, 0.1)",
                              },
                            }}
                          >
                            Remove ID Photo
                          </Button>
                        )}
                      </MDBox>
                    </MDBox>
                  </MDBox>
                </MDBox>
              </Card>
            </Grid>

            {/* Right Column - Form Fields */}
            <Grid item xs={12} md={9}>
              <Card>
                <MDBox p={3}>
                  {/* Basic Information Section */}
                  <MDBox mb={4}>
                    <MDTypography
                      variant="h6"
                      fontWeight="medium"
                      mb={2}
                      display="flex"
                      alignItems="center"
                    >
                      <Icon sx={{ mr: 1, color: "info.main" }}>person</Icon>
                      Basic Information
                    </MDTypography>
                    <Divider sx={{ mb: 3 }} />
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <MDInput
                          fullWidth
                          label="Full Name"
                          name="fullName"
                          value={formData.fullName}
                          onChange={handleChange}
                          required
                          disabled={submitting}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <Icon color="action">badge</Icon>
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <MDInput
                          fullWidth
                          label="Email"
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          required
                          disabled={submitting}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <Icon color="action">email</Icon>
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <MDInput
                          fullWidth
                          label="ID Number"
                          name="idNumber"
                          value={formData.idNumber}
                          onChange={handleChange}
                          required
                          disabled={submitting}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <Icon color="action">credit_card</Icon>
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <FormControl fullWidth required>
                          <InputLabel id="gender-label">Gender</InputLabel>
                          <Select
                            labelId="gender-label"
                            name="gender"
                            value={formData.gender}
                            onChange={handleChange}
                            label="Gender"
                            disabled={submitting}
                            sx={{
                              minHeight: "56px",
                              "& .MuiSelect-select": {
                                display: "flex",
                                alignItems: "center",
                                minHeight: "56px",
                                paddingTop: "16px",
                                paddingBottom: "16px",
                              },
                            }}
                          >
                            <MenuItem value="Male">
                              <Box display="flex" alignItems="center">
                                <Icon sx={{ mr: 1, color: "info.main" }}>male</Icon>
                                Male
                              </Box>
                            </MenuItem>
                            <MenuItem value="Female">
                              <Box display="flex" alignItems="center">
                                <Icon sx={{ mr: 1, color: "error.main" }}>female</Icon>
                                Female
                              </Box>
                            </MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <MDInput
                          fullWidth
                          label="Personal Email"
                          type="email"
                          name="personalEmail"
                          value={formData.personalEmail}
                          onChange={handleChange}
                          disabled={submitting}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <Icon color="action">alternate_email</Icon>
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <FormControl fullWidth>
                          <InputLabel id="nationality-label">Nationality</InputLabel>
                          <Select
                            labelId="nationality-label"
                            name="nationality"
                            value={formData.nationality}
                            onChange={handleChange}
                            label="Nationality"
                            disabled={submitting}
                            sx={{
                              minHeight: "56px",
                              "& .MuiSelect-select": {
                                display: "flex",
                                alignItems: "center",
                                minHeight: "56px",
                                paddingTop: "16px",
                                paddingBottom: "16px",
                              },
                            }}
                          >
                            {NATIONALITIES.map((nat) => (
                              <MenuItem key={nat} value={nat}>
                                {nat}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                    </Grid>
                  </MDBox>

                  {/* Contact Information Section */}
                  <MDBox mb={4}>
                    <MDTypography
                      variant="h6"
                      fontWeight="medium"
                      mb={2}
                      display="flex"
                      alignItems="center"
                    >
                      <Icon sx={{ mr: 1, color: "info.main" }}>phone</Icon>
                      Contact Information
                    </MDTypography>
                    <Divider sx={{ mb: 3 }} />
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <MDInput
                          fullWidth
                          label="Phone Number"
                          name="phone"
                          value={formData.phone}
                          onChange={handleChange}
                          disabled={submitting}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <Icon color="action">phone</Icon>
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <MDInput
                          fullWidth
                          label="Company Number"
                          name="companyNumber"
                          value={formData.companyNumber}
                          onChange={handleChange}
                          required
                          disabled={submitting}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <Icon color="action">business</Icon>
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <MDInput
                          fullWidth
                          label="Relevant Phone Number 1"
                          name="relevantPhone0"
                          value={formData.relevantPhoneNumbers[0] || ""}
                          onChange={handleChange}
                          disabled={submitting}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <Icon color="action">phone_android</Icon>
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <MDInput
                          fullWidth
                          label="Relevant Phone Number 2"
                          name="relevantPhone1"
                          value={formData.relevantPhoneNumbers[1] || ""}
                          onChange={handleChange}
                          disabled={submitting}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <Icon color="action">phone_android</Icon>
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Grid>

                      <Grid item xs={12}>
                        <MDInput
                          fullWidth
                          label="Address"
                          name="address"
                          value={formData.address}
                          onChange={handleChange}
                          disabled={submitting}
                          multiline
                          rows={3}
                          sx={{
                            "& .MuiInputBase-root": {
                              alignItems: "flex-start",
                            },
                            "& .MuiInputBase-input": {
                              paddingTop: "14px",
                            },
                          }}
                        />
                      </Grid>
                    </Grid>
                  </MDBox>

                  {/* Personal Information Section */}
                  <MDBox mb={4}>
                    <MDTypography
                      variant="h6"
                      fontWeight="medium"
                      mb={2}
                      display="flex"
                      alignItems="center"
                    >
                      <Icon sx={{ mr: 1, color: "info.main" }}>family_restroom</Icon>
                      Personal Information
                    </MDTypography>
                    <Divider sx={{ mb: 3 }} />
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <FormControl fullWidth>
                          <InputLabel id="marital-status-label">Marital Status</InputLabel>
                          <Select
                            labelId="marital-status-label"
                            name="maritalStatus"
                            value={formData.maritalStatus}
                            onChange={handleChange}
                            label="Marital Status"
                            disabled={submitting}
                            sx={{
                              minHeight: "56px",
                              "& .MuiSelect-select": {
                                display: "flex",
                                alignItems: "center",
                                minHeight: "56px",
                                paddingTop: "16px",
                                paddingBottom: "16px",
                              },
                            }}
                          >
                            <MenuItem value="Single">
                              <Box display="flex" alignItems="center">
                                <Icon sx={{ mr: 1, color: "info.main" }}>person</Icon>
                                Single
                              </Box>
                            </MenuItem>
                            <MenuItem value="Married">
                              <Box display="flex" alignItems="center">
                                <Icon sx={{ mr: 1, color: "success.main" }}>favorite</Icon>
                                Married
                              </Box>
                            </MenuItem>
                            <MenuItem value="Divorced">
                              <Box display="flex" alignItems="center">
                                <Icon sx={{ mr: 1, color: "warning.main" }}>heart_broken</Icon>
                                Divorced
                              </Box>
                            </MenuItem>
                            <MenuItem value="Widowed">
                              <Box display="flex" alignItems="center">
                                <Icon sx={{ mr: 1, color: "error.main" }}>person_off</Icon>
                                Widowed
                              </Box>
                            </MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <MDInput
                          type="number"
                          label="Family Members Number"
                          fullWidth
                          name="familyMembersNumber"
                          value={formData.familyMembersNumber}
                          onChange={handleChange}
                          disabled={submitting}
                          inputProps={{ min: 0 }}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <Icon color="action">people</Icon>
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Grid>
                    </Grid>
                  </MDBox>

                  {/* Account Information Section */}
                  <MDBox mb={3}>
                    <MDTypography
                      variant="h6"
                      fontWeight="medium"
                      mb={2}
                      display="flex"
                      alignItems="center"
                    >
                      <Icon sx={{ mr: 1, color: "info.main" }}>account_circle</Icon>
                      Account Information
                    </MDTypography>
                    <Divider sx={{ mb: 3 }} />
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <FormControl fullWidth required>
                          <InputLabel id="role-label">Role</InputLabel>
                          <Select
                            labelId="role-label"
                            name="role"
                            value={formData.role}
                            onChange={handleChange}
                            label="Role"
                            disabled={submitting}
                            sx={{
                              minHeight: "56px",
                              "& .MuiSelect-select": {
                                display: "flex",
                                alignItems: "center",
                                minHeight: "56px",
                                paddingTop: "16px",
                                paddingBottom: "16px",
                              },
                            }}
                          >
                            {roles
                              .filter(
                                (role) =>
                                  role.name !== "Property Owner" &&
                                  role.name !== "Property_Owner" &&
                                  role.name !== "Client"
                              )
                              .map((role) => (
                                <MenuItem key={role._id} value={role._id}>
                                  <Box display="flex" alignItems="center" width="100%">
                                    <Icon sx={{ mr: 1, color: "info.main" }}>badge</Icon>
                                    {role.name === "Call_Center_Agent"
                                      ? "Call Center Agent"
                                      : role.name.replace(/_/g, " ")}
                                  </Box>
                                </MenuItem>
                              ))}
                          </Select>
                        </FormControl>
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <MDInput
                          label="Password (Optional)"
                          type="password"
                          fullWidth
                          name="password"
                          value={formData.password}
                          onChange={handleChange}
                          disabled={submitting}
                          helperText="Leave empty to auto-generate password"
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <Icon color="action">lock</Icon>
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Grid>
                    </Grid>
                  </MDBox>

                  {/* Action Buttons */}
                  <MDBox
                    display="flex"
                    justifyContent="flex-end"
                    gap={2}
                    mt={4}
                    pt={3}
                    borderTop="1px solid"
                    borderColor="divider"
                  >
                    <MDButton
                      variant="outlined"
                      color="secondary"
                      onClick={() => navigate("/super-admin/users")}
                      disabled={submitting}
                      startIcon={<Icon>close</Icon>}
                      sx={{
                        color: "white",
                        borderColor: "white",
                        "&:hover": {
                          color: "white",
                          borderColor: "white",
                          backgroundColor: "rgba(255, 255, 255, 0.1)",
                        },
                        "&:disabled": {
                          color: "rgba(255, 255, 255, 0.3)",
                          borderColor: "rgba(255, 255, 255, 0.3)",
                        },
                      }}
                    >
                      Cancel
                    </MDButton>
                    <MDButton
                      variant="gradient"
                      color="success"
                      type="submit"
                      disabled={
                        submitting ||
                        !formData.fullName ||
                        !formData.email ||
                        !formData.role ||
                        !formData.idNumber ||
                        !formData.companyNumber ||
                        !formData.gender ||
                        !photoFile ||
                        !idPhotoFile
                      }
                      startIcon={
                        submitting ? (
                          <CircularProgress size={20} color="inherit" />
                        ) : (
                          <Icon>save</Icon>
                        )
                      }
                      sx={{
                        color: "white",
                        "&:hover": {
                          color: "white",
                        },
                        "&:disabled": {
                          color: "rgba(255, 255, 255, 0.3)",
                        },
                      }}
                    >
                      {submitting ? "Creating User..." : "Create User"}
                    </MDButton>
                  </MDBox>
                </MDBox>
              </Card>
            </Grid>
          </Grid>
        </form>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default CreateUser;
